import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import t, { RTL_LANGS } from "@/i18n/translations";

const AppContext = createContext(null);

// Base currency is TND. Display multipliers (demo fixed rates).
export const CURRENCIES = {
  TND: { code: "TND", symbol: "DT", rate: 1, decimals: 3 },
  EUR: { code: "EUR", symbol: "€", rate: 0.29, decimals: 2 },
  USD: { code: "USD", symbol: "$", rate: 0.32, decimals: 2 },
};

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const supported = ["fr", "ar", "en", "es"];
    const p = new URLSearchParams(window.location.search).get("lang");
    if (p && supported.includes(p)) return p;
    return localStorage.getItem("evoo_lang") || "fr";
  });
  const [currency, setCurrency] = useState(() => localStorage.getItem("evoo_currency") || "TND");

  useEffect(() => {
    localStorage.setItem("evoo_lang", lang);
    const isRtl = RTL_LANGS.includes(lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = isRtl ? "rtl" : "ltr";
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("evoo_currency", currency);
  }, [currency]);

  const translate = useCallback(
    (key, vars) => {
      const dict = t[lang] || t.fr;
      let str = dict[key] ?? t.fr[key] ?? key;
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          str = str.replace(`{${k}}`, v);
        });
      }
      return str;
    },
    [lang]
  );

  const formatPrice = useCallback(
    (tnd) => {
      const c = CURRENCIES[currency];
      const val = tnd * c.rate;
      const formatted = new Intl.NumberFormat(lang === "ar" ? "ar-TN" : lang, {
        minimumFractionDigits: c.decimals,
        maximumFractionDigits: c.decimals,
      }).format(val);
      return `${formatted} ${c.symbol}`;
    },
    [currency, lang]
  );

  const formatNumber = useCallback(
    (n) => new Intl.NumberFormat(lang === "ar" ? "ar-TN" : lang).format(n),
    [lang]
  );

  const isRtl = RTL_LANGS.includes(lang);

  return (
    <AppContext.Provider
      value={{ lang, setLang, currency, setCurrency, t: translate, formatPrice, formatNumber, isRtl }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
